import { fullName, resolveColour, type EntityName } from "odyssey-shared";
import type { NameplateChoices } from "../../settings.ts";

/**
 * What a nameplate shows (P1.9-T24; ADR-065, "the family is one look away"): its form, from the viewer's Nameplates
 * choices; the text that form draws beside the flag mark; and the name's colour, from the character's Infamy tier
 * (name colour belongs to Infamy, ADR-061). All of it is pure and allocates nothing but the full name's text, which
 * a plate asks for only when its name or its form changes.
 */

/**
 * A plate's form: the mark and the first name, the mark and the full name, the mark alone, or no plate.
 */
export type PlateForm = "first" | "full" | "flag" | "none";

/**
 * The name colour of each Infamy tier as a tint, in the Infamy component's order (PROTOCOL.md 5.2): Neutral,
 * Suspect, Criminal and Outlaw.
 */
const TIER_TINTS: readonly number[] = Object.freeze((["infamy-neutral", "infamy-suspect", "infamy-criminal", "infamy-outlaw"] as const).map((role) => Number.parseInt(resolveColour(role).slice(1), 16)));

/**
 * The Neutral name colour as a tint.
 */
const NEUTRAL = TIER_TINTS[0] as number;

/**
 * A plate's form from the viewer's choices. Another player's plate takes the form other players' plates are chosen
 * as, and none while they are off. The player's own plate is none while it is off; on, it takes other players'
 * form, and the first name while their plates are off, so every plate the viewer sees is drawn alike.
 *
 * @param own Whether the plate is the player's own character's.
 * @param choices The viewer's Nameplates choices.
 * @returns The form.
 */
export function plateForm(own: boolean, choices: Readonly<NameplateChoices>): PlateForm {
  if (own) {
    if (choices.own === "off") {
      return "none";
    }
    return choices.others === "off" ? "first" : choices.others;
  }
  return choices.others === "off" ? "none" : choices.others;
}

/**
 * The text a plate draws beside its mark in a form.
 *
 * @param form The form, one that draws a plate.
 * @param name The character's two names.
 * @returns The first name itself, the first and family names joined by one space (the first name alone for a dev
 *   join, whose family part is empty), or the empty text for the mark alone.
 */
export function plateText(form: Exclude<PlateForm, "none">, name: EntityName): string {
  if (form === "first") {
    return name.first;
  }
  return form === "full" ? fullName(name.first, name.family) : "";
}

/**
 * A name's colour from its Infamy tier.
 *
 * @param tier The Infamy component's tier.
 * @returns The tier's name colour as a tint, or the Neutral colour for a tier the page does not know.
 */
export function infamyTint(tier: number): number {
  return TIER_TINTS[tier] ?? NEUTRAL;
}
